import { useEffect, useState } from "react";

import { collection, getDocs, query, where } from "firebase/firestore";

import { db } from "../firebase/firebase";

const useBlogs = (publishedOnly = true) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadBlogs = async () => {
      try {
        setLoading(true);
        setError("");

        const blogsRef = collection(db, "blogs");

        let blogsQuery;

        // =====================================================
        // GET PUBLISHED BLOGS
        // =====================================================

        if (publishedOnly) {
          blogsQuery = query(blogsRef, where("published", "==", true));
        } else {
          blogsQuery = query(blogsRef);
        }

        const snapshot = await getDocs(blogsQuery);

        // =====================================================
        // CONVERT FIRESTORE DOCUMENTS
        // =====================================================

        const blogsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // =====================================================
        // SORT BLOGS
        // =====================================================
        // We sort in JavaScript instead of using Firestore
        // orderBy(). This avoids requiring a composite index.
        // =====================================================

        blogsData.sort((a, b) => {
          const getTime = (value) => {
            if (!value) {
              return 0;
            }

            // Firebase Timestamp
            if (value?.toMillis) {
              return value.toMillis();
            }

            // Firebase Timestamp converted to Date
            if (value?.toDate) {
              return value.toDate().getTime();
            }

            // JavaScript Date
            if (value instanceof Date) {
              return value.getTime();
            }

            // String / number
            const date = new Date(value);

            if (isNaN(date.getTime())) {
              return 0;
            }

            return date.getTime();
          };

          return getTime(b.createdAt) - getTime(a.createdAt);
        });

        if (isMounted) {
          setBlogs(blogsData);
        }
      } catch (err) {
        console.error("Error loading blogs:", err);

        if (isMounted) {
          setError(err.message || "Unable to load blogs.");

          setBlogs([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBlogs();

    return () => {
      isMounted = false;
    };
  }, [publishedOnly]);

  return {
    blogs,
    loading,
    error,
  };
};

export default useBlogs;
